import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getDetailById, getRelated } from "@/utils/content";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function DetailPage() {
  const { id = "" } = useParams();
  const item = getDetailById(id);
  const { toggle, isFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useSeo({
    title: item ? `${item.title} - 风间由美详情` : "内容未找到 - 风间由美官方网站",
    description: item
      ? `${item.summary}（${item.meta}，${item.date}）`
      : "风间由美官方网站内容详情页。",
    keywords: item
      ? `风间由美,${item.title},${item.tags.join(",")},风间由美${item.kind === "news" ? "新闻" : "作品"}`
      : "风间由美,详情",
  });

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 2400);
    return () => clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <div className="mx-auto flex w-full max-w-[1320px] flex-col items-center px-4 py-24 text-center md:px-6">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-primary-500">
          <i className="ri-file-unknow-line text-3xl" />
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">
          没有找到对应的内容
        </h1>
        <p className="mt-3 max-w-md text-sm text-foreground-600">
          该链接可能已失效，或对应的资讯与作品已被移除。你可以返回列表继续浏览。
        </p>
        <div className="mt-7 flex flex-col gap-3 sm:flex-row">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            浏览新闻
          </Link>
          <Link
            to="/works"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            浏览作品
          </Link>
        </div>
      </div>
    );
  }

  const related = getRelated(item, 3);
  const listPath = item.kind === "news" ? "/news" : "/works";
  const listLabel = item.kind === "news" ? "新闻资讯" : "作品介绍";
  const favorited = isFavorite(item.id);

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      }
    } catch {
      /* ignore clipboard errors */
    }
    setToast("已复制当前页面链接，可分享给好友");
  };

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-10 md:px-6 md:py-14">
      <Breadcrumb
        items={[
          { label: "首页", path: "/" },
          { label: listLabel, path: listPath },
          { label: item.title },
        ]}
      />

      <article className="mt-6">
        <div className="grid gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
              <span className="rounded-full bg-primary-500 px-3 py-1 font-semibold text-background-50">
                {item.meta}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {item.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {item.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-price-tag-3-line" />
                {item.source}
              </span>
            </div>

            <h1 className="mt-5 font-heading text-2xl font-bold leading-tight tracking-tight text-foreground-950 md:text-4xl">
              {item.title}
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {item.summary}
            </p>

            <div className="mt-7 overflow-hidden rounded-lg border border-background-200" style={{ height: 380 }}>
              <img
                src={item.image}
                alt={`${item.title} 主图`}
                title={`${item.title} - 风间由美详情`}
                className="h-full w-full object-top"
              />
            </div>

            <div className="mt-8 space-y-5">
              {item.paragraphs.map((p, i) => (
                <p key={i} className="text-sm leading-loose text-foreground-800 md:text-base">
                  {p}
                </p>
              ))}
            </div>

            {item.cast.length > 0 ? (
              <section className="mt-10 rounded-lg border border-background-200 bg-background-100/60 p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-group-line text-primary-500" />
                  演职人员
                </h2>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {item.cast.map((member) => (
                    <div
                      key={member.name + member.role}
                      className="flex items-center gap-4 rounded-md bg-background-50 p-4"
                    >
                      <span className="flex h-11 w-11 items-center justify-center rounded-full bg-accent-100 font-heading text-lg font-bold text-accent-900">
                        {member.name.slice(0, 1)}
                      </span>
                      <div>
                        <p className="font-medium text-foreground-950">{member.name}</p>
                        <p className="mt-0.5 text-xs text-foreground-500">{member.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            ) : null}

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => toggle(item.id)}
                className={`inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 py-2.5 text-sm font-semibold transition-colors ${
                  favorited
                    ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                    : "border border-primary-300 text-primary-600 hover:bg-primary-50"
                }`}
              >
                <i className={favorited ? "ri-heart-fill" : "ri-heart-line"} />
                {favorited ? "已收藏" : "收藏这篇"}
              </button>
              <button
                type="button"
                onClick={share}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
              >
                <i className="ri-share-forward-line" />
                分享链接
              </button>
              <Link
                to={listPath}
                className="inline-flex items-center gap-2 whitespace-nowrap rounded-md px-4 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:text-primary-600"
              >
                <i className="ri-arrow-left-line" />
                返回{listLabel}
              </Link>
            </div>

            <p className="mt-4 flex items-center gap-2 text-xs text-foreground-400">
              <i className="ri-information-line" />
              收藏仅保存在当前浏览器本地，不会上传服务器。
            </p>
          </div>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5 lg:sticky lg:top-24">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-bookmark-3-line text-primary-500" />
                相关内容推荐
              </h2>
              <div className="mt-4 flex flex-col gap-4">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/detail/${rel.id}`}
                    className="group flex cursor-pointer items-start gap-3"
                  >
                    <div
                      className="shrink-0 overflow-hidden rounded-md"
                      style={{ width: 92, height: 64 }}
                    >
                      <img
                        src={rel.image}
                        alt={`${rel.title} 缩略图`}
                        title={`${rel.title} - 相关推荐`}
                        className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                        {rel.title}
                      </p>
                      <p className="mt-1 text-xs text-foreground-500">
                        {rel.meta} · {rel.date}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-2 border-t border-background-200 pt-4">
                {item.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </article>

      {toast ? (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 animate-fade-up rounded-full bg-foreground-950 px-5 py-3 text-sm text-background-50 shadow-none">
          <span className="flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-primary-400" />
            {toast}
          </span>
        </div>
      ) : null}
    </div>
  );
}